import { appConfirm } from "@/lib/appDialog";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Calendar, MapPin, Users, X, Loader2, Trash2, Edit3, Save } from "lucide-react";

const TYPES = [
  { value: "visit",      label: "زيارة ميدانية", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  { value: "workshop",   label: "ورشة عمل",       cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { value: "graduation", label: "حفل تخرّج",      cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { value: "meeting",    label: "اجتماع",         cls: "bg-teal-50 text-teal-800 border-teal-200" },
  { value: "other",      label: "أخرى",           cls: "bg-slate-50 text-slate-700 border-slate-200" },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" }); } catch { return "—"; } };
const typeMeta = (v) => TYPES.find((t) => t.value === v) || TYPES[TYPES.length - 1];

export default function AssociationActivities({ compact = false }) {
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, apps] = await Promise.all([
        api.get("/association/activities?upcoming_only=true"),
        api.get("/applications"),
      ]);
      setItems(a.data.activities || []);
      const uniq = {};
      (apps.data.applications || []).filter((x) => ["accepted", "assigned"].includes(x.status))
        .forEach((x) => { uniq[x.student_id] = x.student_name; });
      setStudents(Object.entries(uniq).map(([id, name]) => ({ id, name })));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeItem = async (id) => {
    if (!await appConfirm("حذف هذا النشاط؟")) return;
    try {
      await api.delete(`/association/activities/${id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const list = compact ? items.slice(0, 4) : items;

  return (
    <div data-testid="assoc-activities">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
          زيارات وأنشطة قادمة
          <span className="text-xs font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full arabic-numerals">{items.length}</span>
        </h3>
        <button onClick={() => setForm({ title: "", type: "visit", scheduled_at: "", duration_minutes: 60, location: "", description: "", student_ids: [] })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-bold hover:bg-teal-600" data-testid="new-activity-btn">
          <Plus className="h-3.5 w-3.5" /> نشاط جديد
        </button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-8"><Loader2 className="h-6 w-6 animate-spin text-teal-700" /></div>
      ) : list.length === 0 ? (
        <div className="p-4 rounded-xl border border-dashed border-slate-300 text-center text-slate-500 text-sm">لا توجد أنشطة مجدولة — أضف أول نشاط.</div>
      ) : (
        <div className="divide-y divide-cream-100" data-testid="activities-list">
          {list.map((a) => {
            const t = typeMeta(a.type);
            return (
              <div key={a.id} className="py-3 flex items-start gap-3" data-testid={`activity-${a.id}`}>
                <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${t.cls}`}>
                  <Calendar className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-slate-900 text-sm">{a.title}</div>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${t.cls}`}>{t.label}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{fmt(a.scheduled_at)}</span>
                    {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location}</span>}
                    {a.student_names?.length > 0 && <span className="inline-flex items-center gap-1 arabic-numerals"><Users className="h-3 w-3" />{a.student_names.length}</span>}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  <button onClick={() => setForm({ ...a, scheduled_at: (a.scheduled_at || "").slice(0, 16) })} className="p-1.5 rounded-lg text-teal-700 hover:bg-teal-50" title="تعديل" data-testid={`edit-${a.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => removeItem(a.id)} className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50" title="حذف" data-testid={`delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form && <ActivityModal form={form} setForm={setForm} students={students} onSaved={() => { setForm(null); load(); }} onClose={() => setForm(null)} />}
    </div>
  );
}

function ActivityModal({ form, setForm, students, onSaved, onClose }) {
  const [saving, setSaving] = useState(false);
  const isEdit = !!form.id;
  const save = async () => {
    if (!form.title?.trim()) { toast.error("عنوان النشاط مطلوب"); return; }
    if (!form.scheduled_at) { toast.error("الموعد مطلوب"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, type: form.type, description: form.description,
        location: form.location, duration_minutes: Number(form.duration_minutes || 60),
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        student_ids: form.student_ids || [],
      };
      if (isEdit) await api.patch(`/association/activities/${form.id}`, payload);
      else await api.post("/association/activities", payload);
      toast.success(isEdit ? "تم تحديث النشاط" : "تم إنشاء النشاط");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const toggleStu = (id) => setForm((f) => ({ ...f, student_ids: (f.student_ids || []).includes(id) ? f.student_ids.filter((x) => x !== id) : [...(f.student_ids || []), id] }));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="activity-modal">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
          <h3 className="font-heading font-bold text-slate-900 text-lg">{isEdit ? "تعديل النشاط" : "نشاط جديد"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <F label="العنوان"><input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="a-title" /></F>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="النوع">
              <select value={form.type || "visit"} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="a-type">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </F>
            <F label="المدة (دقيقة)">
              <input type="number" min={15} max={480} value={form.duration_minutes || 60} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm arabic-numerals" data-testid="a-duration" />
            </F>
          </div>
          <F label="الموعد">
            <input type="datetime-local" value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm arabic-numerals" data-testid="a-when" />
          </F>
          <F label="الموقع (اختياري)"><input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="a-location" /></F>
          <F label="الوصف (اختياري)"><textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="a-desc" /></F>
          <F label={`دعوة طلاب (${(form.student_ids || []).length} محدد)`}>
            <div className="max-h-40 overflow-y-auto border border-slate-300 rounded-lg divide-y divide-slate-100 bg-white">
              {students.length === 0 && <div className="p-3 text-sm text-slate-500 text-center">لا يوجد طلاب مسندون</div>}
              {students.map((s) => (
                <label key={s.id} className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-slate-50 ${(form.student_ids || []).includes(s.id) ? "bg-teal-50" : ""}`}>
                  <input type="checkbox" checked={(form.student_ids || []).includes(s.id)} onChange={() => toggleStu(s.id)} className="rounded" />
                  <span className="text-sm text-slate-900">{s.name}</span>
                </label>
              ))}
            </div>
          </F>
        </div>
        <div className="sticky bottom-0 bg-white px-4 py-3 border-t border-slate-100 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-activity">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isEdit ? "حفظ" : "إنشاء"}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) { return (<div><label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>{children}</div>); }
